import { ColumnsType } from 'antd/lib/table'

import { StatusDisplay } from 'ui'

import { ClaimModel } from 'trellis:api/claim/claim-client'
import {
  ClaimColumnFilterOptions,
  ColumnType,
} from 'trellis:components/_siteWide/table/SmartTableTypes'
import { getStatusOverride } from 'trellis:constants/claimStatusDescriptionData'
import { LDFlags$ } from 'trellis:state/globalState'
import { formatColumnDate } from 'trellis:utilities/general'

import { ClaimGridState } from '../claimTypes'
import { ClaimDetailsModelExtended } from '../context/claimDetailContext'
import {
  ClaimModelExtended,
  useClaimGridContext,
} from '../context/claimGridContext'
import {
  columns as claimColumns,
  claimColumnsMap,
} from '../controls/columnSelection/columnSelectionList'
import { ClaimColumnHelpers } from '../util/claim-column-helpers'
import { getPopoverText } from '../util/status-code-helpers'
import { ClaimDetailColumn, ClaimDetailFilter } from './claimDetailColumn'
import { ClaimRowActionsMenu } from './ClaimRowActionsMenu/ClaimRowActionsMenu'

/**
 * Maps a column name to its corresponding ColumnType configuration
 */
function mapColumnToColumnType(
  column: string,
  state: ClaimGridState,
  options: ClaimColumnFilterOptions,
  customColumnWidths: { [column: string]: number },
  setClaimId: (id: string) => void,
  setDetailsTab: (tab: string) => void,
  realtimeLayoutUpdates: boolean,
  onUnsent: boolean,
): ColumnType {
  const match: ColumnType = {
    dataIndex: column,
    width: (customColumnWidths && customColumnWidths[column]) || 150,
    minWidth: 100,
    maxWidth: 200,
  }

  switch (column) {
    case 'Actions':
      match.noFilter = true
      match.width = 40
      match.title = ' ' // If you leave this blank or null it shows 'Actions' for the title
      match.render = (_: string, record: ClaimModel, index: number) => (
        <ClaimRowActionsMenu
          index={index}
          currentClaim={record}
        />
      )
      match.onCell = () => ({
        // This is overriding the 'setDetail' prop passed to SmartTable for the claim grid
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      })
      match.noSort = true
      break
    case 'Details':
      match.render = (_, row: ClaimDetailsModelExtended) =>
        ClaimDetailColumn(row, setClaimId, setDetailsTab)
      match.filter = ClaimDetailFilter()
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 110
      match.minWidth = 110
      match.maxWidth = 120
      break
    case 'TransmitDate':
      match.noFilter = true
      match.title = onUnsent
        ? claimColumnsMap[column]?.unsentTitle
        : claimColumnsMap[column]?.sentTitle

      match.render = (_: string, record: ClaimModel): string =>
        formatColumnDate(
          record.TransmitDateUtc ? record.TransmitDateUtc : record.TransmitDate,
          true,
        )
      match.minWidth = 175
      match.maxWidth = 200
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 175
      break
    case 'SubscriberName':
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 180
      match.minWidth = 180
      match.maxWidth = 250
      break
    case 'PatientName':
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 160
      match.minWidth = 160
      match.maxWidth = 250
      break
    case 'ServiceDate':
      match.noFilter = true
      match.render = (value: string | number | Date) =>
        formatColumnDate(value?.toString(), false)
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 175
      match.minWidth = 175
      match.maxWidth = 200
      break
    case 'LastUpdate':
      match.noFilter = true
      match.render = (_: string, record: ClaimModel): string =>
        formatColumnDate(
          record.LastUpdateUtc ? record.LastUpdateUtc : record.LastUpdate,
          false,
        )
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 175
      match.minWidth = 175
      match.maxWidth = 200
      break
    case 'Status':
      match.type = 'select'
      match.noFilter = true
      match.render = (value: string, row: ClaimModelExtended) => {
        const tooltipText = row.MappedClaimStatusCodes ? (
          getPopoverText(row.MappedClaimStatusCodes)
        ) : !row.StatusTooltip ? null : (
          <p style={{ color: 'var(--character-primary, #333)' }}>
            {row.StatusTooltip}
          </p>
        )
        return (
          <StatusDisplay
            status={value}
            toolTip={tooltipText}
            type='grid'
            queued={row.Queued}
            getStatusOverride={getStatusOverride}
            whiteBackgroundOverride={true}
          />
        )
      }
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 190
      match.minWidth = 100
      match.maxWidth = 190
      break
    case 'StatusDescription':
      match.render = (value: string, row: ClaimModelExtended) =>
        ClaimColumnHelpers.getStatusDescriptionTooltip(value, row, state)
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 250
      match.minWidth = 190
      match.maxWidth = 350
      break
    case 'Carrier':
      match.type = 'select'
      match.options = options.Carrier
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 175
      match.minWidth = 175
      match.maxWidth = 250
      match.render = (value: string, row: ClaimModelExtended) =>
        ClaimColumnHelpers.renderCarrierName(row, realtimeLayoutUpdates)
      break
    case 'Remarks':
      match.width =
        (customColumnWidths && customColumnWidths[column as string]) || 350
      match.minWidth = 350
      match.maxWidth = 400
      break
    case 'Tin':
      match.type = 'select'
      match.options = options.Tin
      break
    case 'Predetermination':
      match.noFilter = true
      match.type = 'select'
      match.options = options.Predetermination
      break
    case 'TreatmentAddress':
      match.noFilter = true
      match.type = 'select'
      match.options = options.TreatmentAddress
      break
    case 'TreatmentCity':
      match.noFilter = true
      match.type = 'select'
      match.options = options.TreatmentCity
      break
    case 'BillingAddress':
      match.type = 'select'
      match.options = options.BillingAddress
      break
    case 'BillingCity':
      match.noFilter = true
      match.type = 'select'
      match.options = options.BillingCity
      break
    case 'NpiGroup':
      match.noFilter = true
      match.type = 'select'
      match.options = options.NpiGroup
      break
    case 'Install':
      match.noFilter = true
      match.type = 'select'
      match.options = options.Install
      break
    case 'TreatingSignature':
      match.noFilter = true
      match.type = 'select'
      match.options = options.TreatingSignature
      break
    case 'CobFlag':
      match.noFilter = true
      match.type = 'select'
      match.options = options.CobFlag
      break
    case 'Username':
      match.type = 'select'
      match.options = options.Username
      break
    case 'EstimatedPayment':
      match.noFilter = true
      match.title = onUnsent
        ? claimColumnsMap[column]?.unsentTitle
        : claimColumnsMap[column]?.sentTitle
      match.render = (value: string, row: ClaimModelExtended) =>
        row?.EstimatedPayment
          ? `$${(row.EstimatedPayment as number).toFixed(2)}`
          : undefined
      break
    default:
      match.width = customColumnWidths ? customColumnWidths[column] : 150
      match.minWidth = 100
      match.maxWidth = 200
      break
  }
  return match
}

//some fields wouldn't make sense to show in the unsent tab so we'll filter them out
const sentOnlyColumns = claimColumns
  ?.filter((column) => column.hideUnsent)
  .map((column) => column.property)

export const getClaimColumns = (
  state: ClaimGridState,
  options: ClaimColumnFilterOptions,
) => {
  const { setClaimId, setDetailsTab } = useClaimGridContext()

  const realtimeLayoutUpdates = LDFlags$.trellisRealtimeLayoutUpdates.get()

  // Retrieve column widths from local storage to correctly set column width
  const customColumnWidths: { [column: string]: number } = JSON.parse(
    localStorage.getItem('claim-column-widths'),
  )

  const onUnsent = state.ActiveTab === 'Unsent'

  let columns = state.VisibleColumns.map((column: string) =>
    mapColumnToColumnType(
      column,
      state,
      options,
      customColumnWidths,
      setClaimId,
      setDetailsTab,
      realtimeLayoutUpdates,
      onUnsent,
    ),
  ) as ColumnsType<ColumnType>

  if (onUnsent) {
    //remove any sentOnlyColumns
    columns = columns.filter(
      (column) =>
        !('dataIndex' in column) ||
        !sentOnlyColumns.includes(column.dataIndex as string),
    )
  }

  return columns
}
